// Floor-plan measure tool. Click two points to drop ONE measurement (no chains).
// Because only the XZ-plane distance matters, every measurement is shown as its
// two axis components — an L of an X leg and a Z leg — each with its own length
// pill that can be edited independently (an axis-aligned measurement collapses to
// a single leg). Multiple measurements PERSIST; `clear()` removes them all.
// Lengths are formatted by an injected formatter. Snapping is done by the caller.

import * as THREE from "three";
import { type SpriteLabel, makeLabel, setLabel } from "./sprite-label";
import { BORDER_SCALE, VERTEX_FREE, VERTEX_LOCKED, VERTEX_SELECTED } from "./vertex-style";
import { markFloorMutation } from "./history";

const Y = 0.03; // float just above the floor
const COLOR = 0xffd23f;
const RIBBON_W = 0.03; // metres: a thin, subtle band drawn across each segment

// A measure node. When `anchor` is set it's pinned to a model (placement) at a
// fixed point in that model's local frame, so the node — and the measurement —
// follow the model as it moves. (x,z) is the resolved world position (a cache
// for anchored nodes, the source of truth for free nodes); `y` is the height of
// the surface it sits on (0 = floor; an anchored node tracks its model's height,
// so the line stays up with a raised object instead of dropping to the ground).
// `locked` pins the node for value edits: resizing a segment moves the UNLOCKED
// end (see planSegmentResize).
type Anchor = { id: number; lx: number; lz: number };
type Pt = { x: number; z: number; y?: number; anchor?: Anchor; locked?: boolean };

// A node's height (metres above the floor; absent = on the floor).
const py = (p: { y?: number }): number => p.y ?? 0;

// Below this (metres) a component is treated as zero — the measurement is
// axis-aligned and that leg/pill is dropped.
const EPS = 1e-4;

// Which axis component of a measurement a value edit / pill refers to.
export type MeasureComp = "x" | "z";

// Serialized node: a free node is {x,z}; an anchored node is {i,lx,lz} where `i`
// is the placement's INDEX (runtime ids aren't stable across save/undo).
// `l: 1` marks a locked node (either kind).
export type SerMeasurePt = {
  x?: number;
  z?: number;
  i?: number;
  lx?: number;
  lz?: number;
  l?: 1;
};

// A selected segment: chain index + segment index (between points seg, seg+1).
export type MeasureSel = { chain: number; seg: number };
// One component pill of a segment (its X leg or Z leg).
export type MeasurePill = { chain: number; seg: number; comp: MeasureComp };
// A reference to one node (endpoint): chain index + point index.
export type VertexRef = { chain: number; idx: number };

// Resize planning (pure — unit tested). Given a segment a→b and a desired new
// length, decide which endpoint(s) move and by how much:
//   • both ends locked → null (can't resize)
//   • one end locked → only the OTHER end moves (full delta); the locked
//     end's mesh stays put
//   • neither locked → BOTH ends move symmetrically about the midpoint (each by
//     half the delta), so two attached meshes are pushed apart / pulled together
//     equally and the measurement keeps its centre
// The segment's direction is preserved (moved ends slide along the line), so a
// mesh attached to a moving end translates without rotating. Returns null for
// non-positive lengths, a degenerate (zero-length) segment, or when both ends
// are pinned to the SAME model — moving it can't change the length.
export interface ResizeEnd {
  x: number;
  z: number;
  locked?: boolean;
  anchorId?: number;
}
export interface ResizeMove {
  end: "a" | "b";
  dx: number;
  dz: number;
  anchorId?: number;
}
export function planSegmentResize(
  a: ResizeEnd,
  b: ResizeEnd,
  newLen: number,
): ResizeMove[] | null {
  if (!(newLen > 0)) return null;
  if (a.locked && b.locked) return null;
  const ux = b.x - a.x;
  const uz = b.z - a.z;
  const len = Math.hypot(ux, uz);
  if (len < 1e-9) return null; // no direction to resize along
  if (a.anchorId !== undefined && a.anchorId === b.anchorId) return null;
  const k = newLen / len;
  if (a.locked) {
    // Only b moves (full delta): b' = a + k(b - a).
    return [{ end: "b", dx: (k - 1) * ux, dz: (k - 1) * uz, anchorId: b.anchorId }];
  }
  if (b.locked) {
    // Only a moves (full delta): a' = b - k(b - a).
    return [{ end: "a", dx: (1 - k) * ux, dz: (1 - k) * uz, anchorId: a.anchorId }];
  }
  // Neither locked: split the delta — a slides back, b slides forward, equally.
  const h = (k - 1) / 2;
  return [
    { end: "a", dx: -h * ux, dz: -h * uz, anchorId: a.anchorId },
    { end: "b", dx: h * ux, dz: h * uz, anchorId: b.anchorId },
  ];
}

// Connected component (pure — unit tested): every id reachable from `seeds`
// through the undirected `links`, plus the seeds themselves. Used to expand a
// moved placement to its full rigid group of locked-locked measurement links.
export function rigidGroup(
  links: [number, number][],
  seeds: Iterable<number>,
): number[] {
  const adj = new Map<number, number[]>();
  for (const [x, y] of links) {
    (adj.get(x) ?? adj.set(x, []).get(x)!).push(y);
    (adj.get(y) ?? adj.set(y, []).get(y)!).push(x);
  }
  const out = new Set<number>();
  const stack = [...seeds];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (out.has(id)) continue;
    out.add(id);
    for (const n of adj.get(id) ?? []) if (!out.has(n)) stack.push(n);
  }
  return [...out];
}

// Remove segment `seg` from a chain (pure — unit tested): the chain splits at
// the removed span; pieces shorter than a full segment (≤1 point) are dropped.
export function removeChainSegment<T>(chain: T[], seg: number): T[][] {
  const left = chain.slice(0, seg + 1);
  const right = chain.slice(seg + 1);
  const out: T[][] = [];
  if (left.length >= 2) out.push(left);
  if (right.length >= 2) out.push(right);
  return out;
}

export class MeasureSystem {
  readonly group = new THREE.Group();
  // Show/hide every measurement at once (hidden ones are also non-pickable).
  setVisible(v: boolean): void {
    this.group.visible = v;
  }
  // All measured chains. The last one is the "current" chain while `building`;
  // once finished it stays put and the next point starts a fresh chain.
  private chains: Pt[][] = [];
  private building = false;
  // Resolves an anchor to a current world point (null if its model is gone).
  private resolveAnchor:
    | ((a: Anchor) => { x: number; y: number; z: number } | null)
    | null = null;
  private lineGeo = new THREE.BufferGeometry();
  private line: THREE.Mesh;
  private previewGeo = new THREE.BufferGeometry();
  private previewLine: THREE.Mesh;
  // Endpoint dots: a FIXED world size (like the ribbon) so they hold their
  // proportion to the line as you zoom — not billboarded to a constant pixel
  // size the way the snap cursor is. Small (clicked via a screen-space pick).
  // Higher segment counts → a rounder dot (it's small but reads as a circle).
  private dotGeo = new THREE.SphereGeometry(0.03, 28, 20);
  // transparent at FULL opacity (not opaque): the ribbon is transparent and the
  // opaque pass always draws before the transparent one, so opaque dots would be
  // painted over by the ribbon (a washed band through them — worst on a locked
  // red dot). In the transparent pass, renderOrder lifts the dots fully over the
  // ribbon, so they read as solid. (Same reason as selLine below.)
  private dotMat = new THREE.MeshBasicMaterial({
    color: VERTEX_FREE,
    depthTest: false,
    transparent: true,
  });
  // Locked nodes show red (matches the corner/jamb lock colour).
  private lockedDotMat = new THREE.MeshBasicMaterial({
    color: VERTEX_LOCKED,
    depthTest: false,
    transparent: true,
  });
  private dots: THREE.Mesh[] = [];
  // The hovered vertex (Select tool): the dot keeps its size AND its lock-state
  // colour (yellow free / red locked); hovering adds a THIN WHITE border — a
  // slightly larger sphere drawn BEHIND the dot, so only the rim shows. There's
  // no separate "selected" state; a click toggles the dot's lock.
  private hoverVertex: VertexRef | null = null;
  private dotBorderGeo = new THREE.SphereGeometry(0.03 * BORDER_SCALE, 28, 20);
  private hoverVertexBorder = new THREE.Mesh(
    this.dotBorderGeo,
    // transparent too (same pass as the dots) so the ribbon can't tint the rim.
    new THREE.MeshBasicMaterial({ color: VERTEX_SELECTED, depthTest: false, transparent: true }),
  );
  // Selected segment (Select tool): recoloured blue (same width, drawn over the
  // yellow ribbon) + the target of Backspace-delete and the in-place editor.
  private sel: MeasureSel | null = null;
  private selGeo = new THREE.BufferGeometry();
  private selLine: THREE.Mesh;
  // Segment whose midpoint pill is hidden while the edit box stands in for it.
  private hiddenSeg: MeasurePill | null = null;
  private marker = new THREE.Mesh(
    new THREE.SphereGeometry(0.07, 14, 10),
    new THREE.MeshBasicMaterial({ color: 0xffffff, depthTest: false }),
  );
  private labels: SpriteLabel[] = [];

  constructor(private fmt: (metres: number) => string) {
    this.line = new THREE.Mesh(
      this.lineGeo,
      new THREE.MeshBasicMaterial({
        color: COLOR,
        transparent: true,
        opacity: 0.6,
        side: THREE.DoubleSide,
        depthTest: false,
      }),
    );
    this.line.renderOrder = 11;
    // Geometry is rebuilt each frame without recomputing bounds, so a stale
    // bounding sphere would frustum-cull the ribbon (vanishing when zoomed in).
    this.line.frustumCulled = false;
    this.previewLine = new THREE.Mesh(
      this.previewGeo,
      new THREE.MeshBasicMaterial({
        color: COLOR,
        transparent: true,
        opacity: 0.4,
        side: THREE.DoubleSide,
        depthTest: false,
      }),
    );
    this.previewLine.renderOrder = 11;
    this.previewLine.frustumCulled = false;
    this.previewLine.visible = false;
    this.selLine = new THREE.Mesh(
      this.selGeo,
      new THREE.MeshBasicMaterial({
        color: 0x6fa0ff, // selection blue (matches corner / handle selection)
        // transparent (at full opacity) so it shares the base ribbon's render
        // pass — renderOrder only orders WITHIN a pass, and the yellow ribbon is
        // transparent, so an opaque overlay would draw first and be painted over.
        transparent: true,
        opacity: 1,
        side: THREE.DoubleSide,
        depthTest: false,
      }),
    );
    this.selLine.renderOrder = 12; // over the yellow ribbon, under the dots
    this.selLine.frustumCulled = false;
    this.selLine.visible = false;
    this.fillMeasures(this.selGeo, []);
    this.group.add(this.selLine);
    this.hoverVertexBorder.renderOrder = 13; // UNDER the dots (14) so the dot covers its centre, leaving a rim
    this.hoverVertexBorder.visible = false;
    this.group.add(this.hoverVertexBorder);
    this.marker.renderOrder = 16; // snap cursor sits on top of everything
    this.marker.visible = false;
    // Seed empty position attributes so the meshes render safely before any
    // points exist.
    this.fillMeasures(this.lineGeo, []);
    this.fillMeasures(this.previewGeo, []);
    this.group.add(this.line, this.previewLine, this.marker);
  }

  // In the 3D view, measurements should be hidden behind meshes (walls/models)
  // like real geometry — enable depth testing on every measurement material. In
  // the flat 2D plan they're an always-on-top annotation overlay (depthTest off).
  // Idempotent; cheap (a render-state toggle, no shader recompile). Labels are
  // pooled with per-instance materials, so rebuild() applies the flag to any it
  // creates later — kept in `occluded`.
  private occluded = false;
  setOccluded(on: boolean): void {
    if (on === this.occluded) return;
    this.occluded = on;
    const mats: THREE.Material[] = [
      this.line.material as THREE.Material,
      this.previewLine.material as THREE.Material,
      this.selLine.material as THREE.Material,
      this.dotMat,
      this.lockedDotMat,
      this.hoverVertexBorder.material as THREE.Material,
      this.marker.material as THREE.Material,
      ...this.labels.map((l) => l.sprite.material),
    ];
    for (const m of mats) m.depthTest = on;
  }

  // Keep the snap cursor a constant on-screen size (it's a cursor): the caller
  // supplies a per-point scale from the camera zoom/distance. The committed
  // endpoint dots are deliberately NOT scaled here — they hold a fixed world
  // size, matching the ribbon.
  updateMarkerScales(scaleAt: (x: number, z: number) => number): void {
    if (this.marker.visible) {
      this.marker.scale.setScalar(scaleAt(this.marker.position.x, this.marker.position.z));
    }
  }

  // The current chain (last one) — the one being built, or the most recent.
  private currentChain(): Pt[] | null {
    return this.chains.length > 0 ? this.chains[this.chains.length - 1] : null;
  }
  // Points in the current chain (for the "show running total" threshold).
  count(): number {
    return this.currentChain()?.length ?? 0;
  }
  // The point the live preview connects from — only while a chain is in progress,
  // so the first point of a NEW chain doesn't right-angle-snap to the old one.
  lastPoint(): Pt | null {
    if (!this.building) return null;
    const c = this.currentChain();
    return c && c.length > 0 ? c[c.length - 1] : null;
  }
  addPoint(x: number, z: number, anchor?: Anchor): void {
    markFloorMutation();
    // A measurement is a single segment: the first point opens it, the second
    // completes it (and auto-finishes, so the next click starts a fresh one).
    if (!this.building) {
      this.chains.push([]);
      this.building = true;
    }
    const c = this.chains[this.chains.length - 1];
    c.push({ x, z, anchor });
    if (c.length >= 2) this.building = false; // single segment — done
    this.rebuild();
  }

  // Inject the model-anchor resolver (set once). Maps an anchor → world point
  // (x,z) plus the model's height y (so a raised model lifts its node).
  setAnchorResolver(
    fn: (a: Anchor) => { x: number; y: number; z: number } | null,
  ): void {
    this.resolveAnchor = fn;
  }
  // Recompute anchored nodes from their models' current transforms; rebuild the
  // geometry/labels if anything moved. Called each frame in floor mode so a
  // measurement updates live as you drag the models it's pinned to. A node whose
  // model has been deleted loses its anchor and freezes at its last position.
  refreshAnchors(): void {
    if (!this.resolveAnchor) return;
    let changed = false;
    for (const c of this.chains) {
      for (const p of c) {
        if (!p.anchor) continue;
        const w = this.resolveAnchor(p.anchor);
        if (!w) {
          delete p.anchor; // model gone — freeze where it was
        } else if (w.x !== p.x || w.z !== p.z || w.y !== py(p)) {
          p.x = w.x;
          p.z = w.z;
          p.y = w.y;
          changed = true;
        }
      }
    }
    if (changed) this.rebuild();
  }
  // Re-render the geometry + labels without changing any nodes. The label
  // formatter reads the live display unit, so call this after a unit toggle so
  // existing measurements re-format into the new unit.
  refreshLabels(): void {
    this.rebuild();
  }
  // Remove EVERY chain (the explicit "clear measurements" action).
  clear(): void {
    markFloorMutation();
    this.chains = [];
    this.building = false;
    this.sel = null;
    this.hiddenSeg = null;
    this.hoverVertex = null;
    this.rebuild();
    this.previewLine.visible = false;
    this.marker.visible = false;
  }
  // End the current chain (drop its preview); it persists, and the next point
  // starts a new chain. A trailing 1-point chain (just an orphan dot) is dropped.
  finish(): void {
    markFloorMutation();
    if (this.building) {
      const c = this.currentChain();
      if (c && c.length < 2) this.chains.pop();
      this.building = false;
      this.rebuild();
    }
    this.previewLine.visible = false;
    this.marker.visible = false;
  }

  // Snapshot / restore all chains — for floor undo/redo + saving with the plan.
  // Anchors serialize by placement INDEX (`toIndex`); free nodes by world (x,z).
  // An anchor whose model no longer exists (toIndex < 0) degrades to a free node.
  serialize(toIndex: (id: number) => number): SerMeasurePt[][] {
    return this.chains.map((c) =>
      c.map((p) => {
        const lock = p.locked ? ({ l: 1 } as const) : undefined;
        if (p.anchor) {
          const i = toIndex(p.anchor.id);
          if (i >= 0) return { i, lx: p.anchor.lx, lz: p.anchor.lz, ...lock };
        }
        return { x: p.x, z: p.z, ...lock };
      }),
    );
  }
  setChains(chains: SerMeasurePt[][], toId: (i: number) => number | null): void {
    markFloorMutation();
    this.chains = chains.map((c) =>
      c.map((sp) => {
        const locked = sp.l === 1 ? true : undefined;
        if (typeof sp.i === "number") {
          const id = toId(sp.i);
          if (id !== null) {
            return { x: 0, z: 0, anchor: { id, lx: sp.lx ?? 0, lz: sp.lz ?? 0 }, locked };
          }
        }
        return { x: sp.x ?? 0, z: sp.z ?? 0, locked };
      }),
    );
    this.building = false;
    this.sel = null;
    this.hiddenSeg = null;
    this.hoverVertex = null;
    this.refreshAnchors(); // resolve anchored nodes to their models' positions
    this.rebuild();
    this.previewLine.visible = false;
    this.marker.visible = false;
  }

  // Show the snap marker at the (snapped) cursor; when `connect` is true and a
  // chain is in progress, also draw the rubber-band preview from the last
  // point. `connect=false` shows just the marker (e.g. before the FIRST node,
  // so you can see where it'll snap). Pass null to hide everything.
  setCursor(p: Pt | null, connect = true): void {
    if (!p) {
      this.previewLine.visible = false;
      this.marker.visible = false;
      return;
    }
    this.marker.position.set(p.x, py(p) + Y, p.z);
    this.marker.visible = true;
    const a = connect ? this.lastPoint() : null;
    if (!a) {
      this.previewLine.visible = false;
      return;
    }
    // Preview the flat L the committed measurement will become. No length pill
    // while placing — the pills appear once the measurement is dropped.
    this.fillMeasures(this.previewGeo, [{ a, b: p }]);
    this.previewLine.visible = true;
  }

  // ── Selection / locks / value editing (Select tool) ────────────────────────

  selected(): MeasureSel | null {
    return this.sel;
  }
  setSelected(sel: MeasureSel | null): void {
    this.sel = sel && this.segPoints(sel) ? sel : null;
    this.rebuild();
  }
  // The endpoints of a selected segment, or null if it no longer exists.
  private segPoints(sel: MeasureSel): { a: Pt; b: Pt } | null {
    const c = this.chains[sel.chain];
    if (!c || sel.seg < 0 || sel.seg + 1 >= c.length) return null;
    return { a: c[sel.seg], b: c[sel.seg + 1] };
  }
  // One axis component of a segment — its XZ distance, the pill's world spot (the
  // leg midpoint), and the endpoints' lock state, for the in-place value editor.
  componentInfo(
    sel: MeasureSel,
    axis: MeasureComp,
  ): {
    dist: number;
    midX: number;
    midY: number;
    midZ: number;
    aLocked: boolean;
    bLocked: boolean;
  } | null {
    const pts = this.segPoints(sel);
    if (!pts) return null;
    const { a, b } = pts;
    const common = { aLocked: !!a.locked, bLocked: !!b.locked };
    // Both legs ride in the first click's plane (py(a)) — the L is drawn flat.
    if (axis === "x") {
      // X leg: a → (b.x, a.z).
      return { dist: Math.abs(b.x - a.x), midX: (a.x + b.x) / 2, midY: py(a), midZ: a.z, ...common };
    }
    // Z leg: (b.x, a.z) → (b.x, b.z).
    return {
      dist: Math.abs(b.z - a.z),
      midX: b.x,
      midY: py(a),
      midZ: (a.z + b.z) / 2,
      ...common,
    };
  }

  // Pick a measurement under the cursor: endpoint dots win (smaller targets),
  // then the midpoint pills, then the ribbon line itself within `tol` metres of
  // the floor point (fx,fz). Returns the POINT for dots (for lock toggling) —
  // callers select the adjacent segment.
  pick(
    ray: THREE.Raycaster,
    fx: number | null,
    fz: number | null,
    tol: number,
    maxDist = Infinity, // ignore dots/pills farther than this along the ray
  ):
    | { kind: "point"; chain: number; idx: number }
    | { kind: "segment"; chain: number; seg: number }
    | null {
    const p = this.pickPoint(ray, maxDist);
    if (p) return { kind: "point", ...p };
    const s = this.pickLabel(ray, maxDist);
    if (s) return { kind: "segment", chain: s.chain, seg: s.seg };
    if (fx !== null && fz !== null) {
      const near = this.segmentNear(fx, fz, tol);
      if (near) return { kind: "segment", ...near };
    }
    return null;
  }
  // Distance from (px,pz) to the segment [a0..a1] in XZ.
  private distToLeg(
    px: number,
    pz: number,
    ax: number,
    az: number,
    bx: number,
    bz: number,
  ): number {
    const dx = bx - ax;
    const dz = bz - az;
    const lenSq = dx * dx + dz * dz;
    if (lenSq < 1e-12) return Math.hypot(px - ax, pz - az);
    const t = Math.max(0, Math.min(1, ((px - ax) * dx + (pz - az) * dz) / lenSq));
    return Math.hypot(px - (ax + t * dx), pz - (az + t * dz));
  }
  pickPoint(ray: THREE.Raycaster, maxDist = Infinity): { chain: number; idx: number } | null {
    const hits = ray.intersectObjects(this.dots, false);
    const hit = hits.find((h) => h.object.visible && h.distance <= maxDist);
    return hit ? (hit.object.userData.pt as { chain: number; idx: number }) : null;
  }
  // Nearest endpoint within `tol` metres of a floor point — a forgiving target
  // for lock toggling (the dots are small, so a raycast alone is fiddly).
  pickEndpoint(x: number, z: number, tol: number): { chain: number; idx: number } | null {
    let best: { chain: number; idx: number } | null = null;
    let bestD = tol;
    for (let ci = 0; ci < this.chains.length; ci++) {
      const c = this.chains[ci];
      for (let i = 0; i < c.length; i++) {
        const d = Math.hypot(x - c[i].x, z - c[i].z);
        if (d < bestD) {
          bestD = d;
          best = { chain: ci, idx: i };
        }
      }
    }
    return best;
  }
  pickLabel(
    ray: THREE.Raycaster,
    maxDist = Infinity,
  ): { chain: number; seg: number; comp: MeasureComp } | null {
    const hits = ray.intersectObjects(
      this.labels.map((l) => l.sprite),
      false,
    );
    const hit = hits.find((h) => h.object.visible && h.distance <= maxDist);
    return hit
      ? (hit.object.userData.pill as { chain: number; seg: number; comp: MeasureComp })
      : null;
  }

  // Pairs of placement ids joined by a measurement SEGMENT whose BOTH endpoints
  // are locked and anchored to (different) models — a rigid link. Moving one
  // such model should move the other so the measured distance is preserved.
  rigidLinks(): [number, number][] {
    const links: [number, number][] = [];
    for (const c of this.chains) {
      for (let i = 0; i + 1 < c.length; i++) {
        const a = c[i];
        const b = c[i + 1];
        if (
          a.locked &&
          b.locked &&
          a.anchor &&
          b.anchor &&
          a.anchor.id !== b.anchor.id
        ) {
          links.push([a.anchor.id, b.anchor.id]);
        }
      }
    }
    return links;
  }
  private segmentNear(x: number, z: number, tol: number): MeasureSel | null {
    let best: MeasureSel | null = null;
    let bestD = tol;
    for (let ci = 0; ci < this.chains.length; ci++) {
      const c = this.chains[ci];
      for (let i = 0; i + 1 < c.length; i++) {
        const a = c[i];
        const b = c[i + 1];
        // Pick against the L legs (a→corner→b), not the diagonal — that's the
        // shape actually drawn.
        const cx = b.x;
        const cz = a.z;
        const d = Math.min(
          this.distToLeg(x, z, a.x, a.z, cx, cz), // X leg
          this.distToLeg(x, z, cx, cz, b.x, b.z), // Z leg
        );
        if (d < bestD) {
          bestD = d;
          best = { chain: ci, seg: i };
        }
      }
    }
    return best;
  }

  // Toggle a node's value-edit lock. Returns the new state (null: no such node).
  togglePointLock(chain: number, idx: number): boolean | null {
    markFloorMutation();
    const p = this.chains[chain]?.[idx];
    if (!p) return null;
    p.locked = !p.locked;
    this.rebuild();
    return !!p.locked;
  }
  vertexLocked(chain: number, idx: number): boolean {
    return !!this.chains[chain]?.[idx]?.locked;
  }

  // The hovered vertex (Select tool) — a white border ring. Cheap to call every
  // frame: it only rebuilds when the hovered vertex actually changes.
  setHoveredVertex(v: VertexRef | null): void {
    const next = v && this.chains[v.chain]?.[v.idx] ? v : null;
    if (next?.chain === this.hoverVertex?.chain && next?.idx === this.hoverVertex?.idx) return;
    this.hoverVertex = next;
    this.rebuild();
  }

  // Reposition a node (Select-tool vertex drag). A FREE node takes the new world
  // point; pass an anchor to re-pin it to a model, or omit/undefined to detach.
  setNode(
    chain: number,
    idx: number,
    x: number,
    z: number,
    anchor?: Anchor,
  ): void {
    markFloorMutation();
    const p = this.chains[chain]?.[idx];
    if (!p) return;
    p.x = x;
    p.z = z;
    if (anchor) {
      p.anchor = anchor; // height is resolved from the model by refreshAnchors
    } else {
      delete p.anchor;
      delete p.y; // back on the floor
    }
    this.rebuild();
  }

  // Plan a value edit of a segment to `newLen` metres → the list of node moves
  // the caller applies (one move per unlocked end; two when neither is locked).
  // An anchored end moves its MODEL (translate, orientation kept — refreshAnchors
  // then re-resolves the node); a free end moves via movePointBy. Null when the
  // edit isn't possible (both ends locked / same model / degenerate).
  planResize(
    sel: MeasureSel,
    axis: MeasureComp,
    newDist: number,
  ): { chain: number; idx: number; dx: number; dz: number; anchorId?: number }[] | null {
    const pts = this.segPoints(sel);
    if (!pts) return null;
    const { a, b } = pts;
    // Project onto the chosen axis and reuse the 1-D resize planner, so the
    // lock / symmetric-split rules carry over; the moves come back along that
    // axis only (the other delta is 0).
    const proj = (p: Pt) =>
      axis === "x" ? { x: p.x, z: 0 } : { x: 0, z: p.z };
    const moves = planSegmentResize(
      { ...proj(a), locked: a.locked, anchorId: a.anchor?.id },
      { ...proj(b), locked: b.locked, anchorId: b.anchor?.id },
      newDist,
    );
    if (!moves) return null;
    return moves.map((m) => ({
      chain: sel.chain,
      idx: m.end === "a" ? sel.seg : sel.seg + 1,
      dx: m.dx,
      dz: m.dz,
      anchorId: m.anchorId,
    }));
  }
  // Move a FREE node by a delta (value edits; anchored nodes follow their model
  // instead). Moving a node that other segments share moves those ends too —
  // that's the chain staying connected.
  movePointBy(chain: number, idx: number, dx: number, dz: number): void {
    markFloorMutation();
    const p = this.chains[chain]?.[idx];
    if (!p || p.anchor) return;
    p.x += dx;
    p.z += dz;
    this.rebuild();
  }

  // Delete the selected segment. The chain splits at the removed span; stub
  // pieces (a single orphan point) are dropped. Returns false if none selected.
  deleteSelected(): boolean {
    markFloorMutation();
    if (!this.sel) return false;
    const c = this.chains[this.sel.chain];
    if (!c) {
      this.sel = null;
      return false;
    }
    const pieces = removeChainSegment(c, this.sel.seg);
    this.chains.splice(this.sel.chain, 1, ...pieces);
    this.sel = null;
    this.hiddenSeg = null;
    this.building = false;
    this.rebuild();
    return true;
  }

  // Hide one component pill while the in-place edit box stands in for it.
  setHiddenSeg(pill: MeasurePill | null): void {
    this.hiddenSeg = pill;
    this.rebuild();
  }

  // The flat-plane height of a chain — the surface height of its FIRST click, so
  // the whole measurement (legs, dots, pills) rides in that one plane.
  private baseY(chain: number): number {
    return py(this.chains[chain]?.[0] ?? {});
  }
  // Consecutive point pairs within each chain — one entry per measurement
  // segment (chains don't bridge each other).
  private segPairs(chains: Pt[][]): { a: Pt; b: Pt }[] {
    const out: { a: Pt; b: Pt }[] = [];
    for (const c of chains) {
      for (let i = 0; i + 1 < c.length; i++) out.push({ a: c[i], b: c[i + 1] });
    }
    return out;
  }

  // Fill a geometry with each measurement's flat L line. Because only the XZ
  // distance matters, the WHOLE L is drawn in ONE plane — the surface of the
  // first click (y = py(a)) — even when b sits at a different height; the legs
  // never slope. A diagonal is the two thin leg bands (the X leg + the Z leg),
  // tiled so the right-angle join is one clean square corner — no notch, and no
  // double-drawn overlap. An axis-aligned measurement is a single straight band.
  private fillMeasures(geo: THREE.BufferGeometry, segs: { a: Pt; b: Pt }[], width = RIBBON_W): void {
    const pos: number[] = [];
    const half = width / 2;
    // An axis-aligned rectangle (two corners) at height y → two triangles.
    const rect = (y: number, x0: number, z0: number, x1: number, z1: number): void => {
      pos.push(
        x0, y, z0, x1, y, z0, x1, y, z1,
        x0, y, z0, x1, y, z1, x0, y, z1,
      );
    };
    for (const { a, b } of segs) {
      const y = py(a) + Y; // flat on the first click's surface plane
      const hasX = Math.abs(b.x - a.x) > EPS;
      const hasZ = Math.abs(b.z - a.z) > EPS;
      if (hasX && hasZ) {
        const sx = Math.sign(b.x - a.x);
        const sz = Math.sign(b.z - a.z);
        // X leg: the horizontal band, extended half a width past the corner so it
        // OWNS the corner square (giving a clean square join).
        rect(y, a.x, a.z - half, b.x + sx * half, a.z + half);
        // Z leg: the vertical band, starting just past the corner square (so the
        // two never overlap) and running to b.
        rect(y, b.x - half, a.z + sz * half, b.x + half, b.z);
      } else if (hasX || hasZ) {
        // Axis-aligned: a single flat band a→b in the a plane.
        let nx = -(b.z - a.z);
        let nz = b.x - a.x;
        const len = Math.hypot(nx, nz);
        if (len < 1e-6) continue;
        nx = (nx / len) * half;
        nz = (nz / len) * half;
        pos.push(
          a.x - nx, y, a.z - nz, a.x + nx, y, a.z + nz, b.x + nx, y, b.z + nz,
          a.x - nx, y, a.z - nz, b.x + nx, y, b.z + nz, b.x - nx, y, b.z - nz,
        );
      }
    }
    geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  }

  private rebuild(): void {
    // The selection must still point at a real segment (deletes/undo move things).
    if (this.sel && !this.segPoints(this.sel)) this.sel = null;
    if (this.hiddenSeg && !this.segPoints(this.hiddenSeg)) this.hiddenSeg = null;
    if (this.hoverVertex && !this.chains[this.hoverVertex.chain]?.[this.hoverVertex.idx]) {
      this.hoverVertex = null;
    }
    // Hovered vertex: a thin WHITE border behind the dot (the dot keeps its own
    // size + lock-state colour; the rim is the only change). It rides in its
    // measurement's flat plane (the first click's height) like the dot it backs.
    const hv = this.hoverVertex
      ? this.chains[this.hoverVertex.chain][this.hoverVertex.idx]
      : null;
    this.hoverVertexBorder.visible = hv !== null;
    if (hv) {
      this.hoverVertexBorder.position.set(hv.x, this.baseY(this.hoverVertex!.chain) + Y, hv.z);
    }
    // Committed bands (every measurement as a flat mitred L) + the selected
    // segment's brighter overlay.
    this.fillMeasures(this.lineGeo, this.segPairs(this.chains));
    const selPts = this.sel ? this.segPoints(this.sel) : null;
    this.selLine.visible = selPts !== null;
    // Same shape as the base band — it reads as the L turning blue, not a
    // separate overlay.
    this.fillMeasures(this.selGeo, selPts ? [{ a: selPts.a, b: selPts.b }] : []);
    // Point dots (pool) — every point across every chain. Each dot remembers its
    // (chain, idx) for picking; locked nodes show the red lock material.
    const allPts: { p: Pt; chain: number; idx: number }[] = [];
    for (let ci = 0; ci < this.chains.length; ci++) {
      const c = this.chains[ci];
      for (let i = 0; i < c.length; i++) allPts.push({ p: c[i], chain: ci, idx: i });
    }
    while (this.dots.length < allPts.length) {
      const d = new THREE.Mesh(this.dotGeo, this.dotMat);
      d.renderOrder = 14; // over the selection border (13) so it covers its centre
      this.dots.push(d);
      this.group.add(d);
    }
    for (let i = 0; i < this.dots.length; i++) {
      const on = i < allPts.length;
      this.dots[i].visible = on;
      if (on) {
        const { p, chain, idx } = allPts[i];
        // Dots sit in their measurement's flat plane (the first click's height),
        // so an endpoint on a raised object drops to the line rather than floating.
        this.dots[i].position.set(p.x, this.baseY(chain) + Y, p.z);
        this.dots[i].material = p.locked ? this.lockedDotMat : this.dotMat;
        this.dots[i].userData.pt = { chain, idx };
      }
    }
    // Component pills (pool) — each measurement contributes up to TWO pills: an
    // X-leg length pill and a Z-leg length pill (an axis-aligned measurement only
    // emits the one non-zero leg). Each remembers its (chain, seg, comp) for
    // click-to-edit value editing; the one the edit box stands in for stays hidden.
    const pills: {
      x: number;
      y: number;
      z: number;
      len: number;
      chain: number;
      seg: number;
      comp: MeasureComp;
    }[] = [];
    for (let ci = 0; ci < this.chains.length; ci++) {
      const c = this.chains[ci];
      for (let si = 0; si + 1 < c.length; si++) {
        for (const comp of ["x", "z"] as MeasureComp[]) {
          const info = this.componentInfo({ chain: ci, seg: si }, comp);
          if (!info || info.dist <= EPS) continue;
          pills.push({
            x: info.midX,
            y: info.midY,
            z: info.midZ,
            len: info.dist,
            chain: ci,
            seg: si,
            comp,
          });
        }
      }
    }
    while (this.labels.length < pills.length) {
      const l = makeLabel();
      l.sprite.material.depthTest = this.occluded; // match the current view (3D occludes)
      this.labels.push(l);
      this.group.add(l.sprite);
    }
    for (let i = 0; i < this.labels.length; i++) {
      const s = i < pills.length ? pills[i] : null;
      const hidden =
        s !== null &&
        this.hiddenSeg !== null &&
        s.chain === this.hiddenSeg.chain &&
        s.seg === this.hiddenSeg.seg &&
        s.comp === this.hiddenSeg.comp;
      this.labels[i].sprite.visible = s !== null && !hidden;
      if (s) {
        setLabel(this.labels[i], this.fmt(s.len));
        this.labels[i].sprite.position.set(s.x, s.y + Y + 0.12, s.z);
        this.labels[i].sprite.userData.pill = { chain: s.chain, seg: s.seg, comp: s.comp };
      }
    }
  }
}
